import { Box, Stack } from '@mui/material';
import { colors, IconButton, Typography, Button } from '@sweep-io/sweep-design';
import { ArrowDown, Close } from '@sweep-io/sweep-design/dist/icons';
import { useState, useMemo, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { TextFieldWithButton } from '../../../../common/ai-chat/input-atoms/TextFieldWithButton';
import { usePanels } from '../../../../panels/usePanels';
import { AiChatMessages } from '../../../../common/ai-chat/AiChatMessages';
import { AiChatAssistantMessageBoxSimple } from '../../../../common/ai-chat/message-atoms/AiChatAssistantMessageBoxSimple';
import { useMetadataMapping } from '../useMetadataMapping';
import { usePageHelpers } from '../usePageHelpers';
import { PanelEnvSelector } from '../../../environments/connect-env/PanelEnvSelector';
import useContainerWithScrollArrow from '../../../../../hooks/useContainerWithScrollArrow';
import { selectHasUserMessages } from '../../../../../reducers/metadataMappingReducer';
import { ChatCompletionRequestMessageRole } from '@server/ai';
import useConfirm from '../../../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { UndoMessageConfirmDialog } from './UndoMessageConfirmDialog';

const GREETING_MESSAGE =
  'I can help you shape this canvas.<br>' +
  'Ask me to add or remove business processes or metadata, or move things around.';

interface MetadataMappingPanelProps {
  onClose: () => void;
}

export const MetadataMappingChatPanel = ({ onClose }: MetadataMappingPanelProps) => {
  const { funnelMapId, crmOrgId } = usePageHelpers();
  const { forceCloseLeftPanel } = usePanels();
  const { openConfirm, onConfirm, onCancel } = useConfirm();
  const {
    messages,
    isLoading,
    error,
    canSendMessage,
    sendMessage,
    status,
    complete,
    undoLastMessage,
    canUndoLastMessage,
  } = useMetadataMapping(funnelMapId || '');
  const [inputValue, setInputValue] = useState('');
  const showGreetingRef = useRef(messages.length === 0);
  const shouldFocusInputRef = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasUserMessages = useSelector(selectHasUserMessages(funnelMapId || ''));

  const handleUndoClick = async () => {
    const isConfirmed = await openConfirm(
      <UndoMessageConfirmDialog onConfirm={onConfirm} onCancel={onCancel} />,
    );

    if (isConfirmed) {
      undoLastMessage();
      // Put the last user message in the input so user can edit and re-send
      const lastUserMessage = [...messages]
        .reverse()
        .find((m) => m.role === ChatCompletionRequestMessageRole.USER);

      if (lastUserMessage && typeof lastUserMessage.content === 'string') {
        setInputValue(lastUserMessage.content);
        shouldFocusInputRef.current = true;
      }
    }
  };

  // Focus input after undo
  useEffect(() => {
    if (shouldFocusInputRef.current && inputValue && inputRef.current) {
      shouldFocusInputRef.current = false;
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          // Move cursor to end of text
          inputRef.current.setSelectionRange(inputValue.length, inputValue.length);
        }
      }, 0);
    }
  }, [inputValue]);

  useEffect(() => {
    if (status === 'completedFetchingData') {
      complete();
    }
  }, [status, complete]);

  const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
  const lastMessageContent = useMemo(() => lastMessage?.content, [lastMessage]);

  const { showScrollButton, scrollToBottom, containerRef } = useContainerWithScrollArrow({
    recalculateWhenThisChange: lastMessageContent,
  });

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <PanelEnvSelector filterByEnvsUsedInCanvas />
      <Box
        sx={{
          height: '56px',
          borderBottom: `1px solid ${colors.grey[120]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
        }}
      >
        <Typography variant="body-medium">Metadata Mapping Agent</Typography>
        <IconButton variant="flat" onClick={onClose} size="tiny">
          <Close />
        </IconButton>
      </Box>

      <Stack flex={1} overflow="hidden" position="relative">
        <Box
          ref={containerRef}
          sx={{
            flex: 1,
            overflow: 'auto',
            padding: '24px',
            paddingLeft: '12px',
          }}
        >
          <Stack gap="16px">
            {showGreetingRef.current && (
              <AiChatAssistantMessageBoxSimple
                content={GREETING_MESSAGE}
                color={hasUserMessages ? undefined : colors.grey[700]}
              />
            )}
            <AiChatMessages
              containerRef={containerRef}
              messages={messages}
              renderInitialFetchMessage={false}
              renderChatPaywallBlockMessage={false}
              renderLoader={isLoading}
              errorMsg={error}
              spacerFix={35}
              crmOrgId={crmOrgId || ''}
              lastUserMessageAction={
                canUndoLastMessage ? (
                  <Button
                    variant="system"
                    size="tiny"
                    onClick={handleUndoClick}
                    startIconName="ArrowBendDownLeft"
                  >
                    Undo
                  </Button>
                ) : undefined
              }
            />
          </Stack>
        </Box>

        {showScrollButton && (
          <Stack
            sx={{
              position: 'absolute',
              bottom: '12px',
              width: '100%',
              alignItems: 'center',
            }}
          >
            <IconButton onClick={scrollToBottom} variant="system" size="small">
              <ArrowDown />
            </IconButton>
          </Stack>
        )}
      </Stack>

      <Box
        sx={{
          padding: '16px',
        }}
      >
        <TextFieldWithButton
          value={inputValue}
          setValue={setInputValue}
          onConfirm={async (message) => {
            forceCloseLeftPanel();
            await sendMessage(message);
          }}
          inputPlaceholder="Ask for changes"
          isLoading={isLoading}
          readonly={!canSendMessage}
          inputRef={inputRef}
        />
      </Box>
    </Box>
  );
};
